import * as React from "react";
import { view } from "@orderofchaos/mvvm";
import { Layout } from "@/components/Layout/Layout";
import type { Product } from "@/stores/Product";
import styles from "./ProductPage.module.css";
import Button, { ButtonDesign } from "@/components/Button/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog/ConfirmDialog";
import {
  ProductPageViewModel,
  type ProductPageViewProps,
} from "./ProductPageViewModel";
import { Measures } from "@/types";
import { formatTypeTag, normalizeTypeTags } from "@/utils/typeTags";
import { ProductPhotoSlider } from "@/components/ProductPhotoSlider/ProductPhotoSlider";

interface ProductPageProps {
  product: Product;
}

const QUANTITY_FORMATTER = new Intl.NumberFormat("ru-RU", {
  maximumFractionDigits: 3,
});

function formatMeasureLabel(measure: Measures): string {
  if (measure === Measures.weight) return "Вес";
  if (measure === Measures.liters) return "Объем";
  return "Количество";
}

function formatQuantity(value: number, measure: Measures): string {
  const normalized = Number.isFinite(value) ? value : 1;
  const formatted = QUANTITY_FORMATTER.format(normalized);

  if (measure === Measures.weight) return `${formatted} кг`;
  if (measure === Measures.liters) return `${formatted} л`;
  return `${formatted} шт`;
}

export const ProductPage: React.FC<ProductPageProps> = view(
  ProductPageViewModel,
)<ProductPageViewProps>(({
  viewModel: {
    product,
    productLocation,
    historyItems,
    isHistoryLoading,
    isTaking,
    isPutting,
    isDestroying,
    isDestroyConfirmOpen,
    actionError,
    canTake,
    canPut,
    canDestroy,
    showDestroyAction,
    isProductDeleted,
    take,
    put,
    openDestroyConfirm,
    closeDestroyConfirm,
    confirmDestroyProduct,
    loadHistory,
    openEditProduct,
    openTypeTagFilter,
    openMeasureFilter,
    openLocationReference,
  },
}) => {
  const {
    photo,
    photos,
    name,
    description,
    manufacturingDate,
    expiryDate,
    typeTags,
    measure,
    quantity,
  } = product;
  const quantityLabel = formatQuantity(quantity, measure);
  const measureLabel = formatMeasureLabel(measure);
  const normalizedTypeTags = normalizeTypeTags(typeTags);
  const productName = name?.trim() || "без названия";

  return (
    <Layout title={name}>
      <article className={styles.page}>
        <section className={styles.heroCard}>
          <div className={styles.media}>
            <ProductPhotoSlider
              photo={photo}
              photos={photos}
              alt={productName}
              className={styles.heroSlider}
              trackClassName={styles.heroSliderTrack}
              imageClassName={styles.heroSliderImage}
              emptyClassName={styles.heroSliderEmpty}
              eagerFirstImage
            />
          </div>
          <div className={styles.heroContent}>
            <div className={styles.heroTitleRow}>
              <h2 className={styles.heroTitle}>{name || "Без названия"}</h2>
              <span className={styles.quantityBadge}>{quantityLabel}</span>
            </div>
            <p className={styles.heroDescription}>
              {description || "Добавьте описание, чтобы быстрее находить товар"}
            </p>
            <div className={styles.heroChips}>
              {normalizedTypeTags.length > 0 ? (
                normalizedTypeTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    className={`${styles.chip} ${styles.chipButton}`}
                    onClick={() => openTypeTagFilter(tag)}
                  >
                    {formatTypeTag(tag)}
                  </button>
                ))
              ) : (
                <span className={styles.chip}>без тега</span>
              )}
              <button
                type="button"
                className={`${styles.chip} ${styles.chipButton}`}
                onClick={openMeasureFilter}
              >
                {measureLabel}
              </button>
              <button
                type="button"
                className={`${styles.chip} ${styles.chipButton}`}
                onClick={openLocationReference}
              >
                {productLocation}
              </button>
            </div>
          </div>
        </section>

        <section className={styles.detailsCard}>
          <div className={styles.detailsHeader}>
            <h3 className={styles.detailsTitle}>Параметры товара</h3>
            <Button
              design={ButtonDesign.ghost}
              className={styles.refresh}
              onClick={openEditProduct}
              disabled={isTaking || isPutting || isDestroying}
            >
              редактировать
            </Button>
          </div>

          <dl className={styles.detailsGrid}>
            <div className={`${styles.detailItem} ${styles.detailItemWide}`}>
              <dt className={styles.detailKey}>Описание</dt>
              <dd className={styles.detailValue}>{description || "—"}</dd>
            </div>
            <div className={styles.detailItem}>
              <dt className={styles.detailKey}>Дата производства</dt>
              <dd className={styles.detailValue}>
                {manufacturingDate ? manufacturingDate.dateString : "—"}
              </dd>
            </div>
            <div className={styles.detailItem}>
              <dt className={styles.detailKey}>Срок годности</dt>
              <dd className={styles.detailValue}>
                {expiryDate ? expiryDate.dateString : "—"}
              </dd>
            </div>
            <div className={styles.detailItem}>
              <dt className={styles.detailKey}>Типы</dt>
              <dd className={styles.detailValue}>
                {normalizedTypeTags.length > 0
                  ? normalizedTypeTags
                      .map((tag) => formatTypeTag(tag))
                      .join(" ")
                  : "—"}
              </dd>
            </div>
            <div className={styles.detailItem}>
              <dt className={styles.detailKey}>Единица</dt>
              <dd className={styles.detailValue}>{measureLabel}</dd>
            </div>
            <div className={styles.detailItem}>
              <dt className={styles.detailKey}>Количество</dt>
              <dd className={styles.detailValue}>{quantityLabel}</dd>
            </div>
            <div className={`${styles.detailItem} ${styles.detailItemWide}`}>
              <dt className={styles.detailKey}>Расположение</dt>
              <dd className={styles.detailValue}>{productLocation}</dd>
            </div>
          </dl>
        </section>

        <section className={styles.actionsCard}>
          <h3 className={styles.actionsTitle}>Действия</h3>
          <div className={styles.buttons}>
            <Button
              design={ButtonDesign.primary}
              onClick={() => void take()}
              disabled={!canTake}
              isLoading={isTaking}
            >
              взять
            </Button>
            <Button
              design={ButtonDesign.basic}
              onClick={() => void put()}
              disabled={!canPut}
              isLoading={isPutting}
            >
              положить
            </Button>
            {showDestroyAction && (
              <Button
                design={ButtonDesign.basic}
                className={styles.destroyButton}
                onClick={openDestroyConfirm}
                disabled={!canDestroy}
                isLoading={isDestroying}
              >
                уничтожить
              </Button>
            )}
          </div>
          {isProductDeleted ? (
            <p className={styles.actionHint}>
              Товар удален. Операции с карточки недоступны.
            </p>
          ) : (
            !canTake &&
            !canPut && (
              <p className={styles.actionHint}>
                Действия станут доступны, когда товар можно будет переместить.
              </p>
            )
          )}
          {actionError && <p className={styles.error}>{actionError}</p>}
        </section>

        <section className={styles.historyCard}>
          <div className={styles.historyHeader}>
            <h3 className={styles.historyTitle}>История операций</h3>
            <Button
              design={ButtonDesign.ghost}
              className={styles.refresh}
              onClick={() => void loadHistory()}
              disabled={isHistoryLoading}
            >
              обновить
            </Button>
          </div>

          {isHistoryLoading && (
            <p className={styles.historyLoading}>загружаем историю...</p>
          )}

          {!isHistoryLoading && historyItems.length === 0 && (
            <p className={styles.historyEmpty}>операций пока нет</p>
          )}

          {!isHistoryLoading && historyItems.length > 0 && (
            <ul className={styles.historyList}>
              {historyItems.map((item) => (
                <li key={item.id} className={styles.historyItem}>
                  <div className={styles.historyTop}>
                    <p className={styles.historyAction}>{item.title}</p>
                    <span
                      className={
                        item.actorType === "system"
                          ? `${styles.historySourceBadge} ${styles.historySourceSystem}`
                          : `${styles.historySourceBadge} ${styles.historySourceUser}`
                      }
                    >
                      {item.actorType === "system"
                        ? "системное"
                        : "пользователь"}
                    </span>
                  </div>
                  <p className={styles.historyMeta}>{item.subtitle}</p>
                  <p className={styles.historyMeta}>автор: {item.actorLabel}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </article>
      <ConfirmDialog
        isOpen={isDestroyConfirmOpen}
        title={`Уничтожить товар "${productName}"?`}
        description={
          "Товар будет перемещен в удаленные. Для полного удаления потребуется подтверждение администратора."
        }
        reasonPrompt={"Причина удаления"}
        reasonPlaceholder={
          "Например: испорчен, утерян, списан после инвентаризации"
        }
        reasonRequired={true}
        reasonMinLength={3}
        reasonMaxLength={280}
        confirmLabel={"уничтожить"}
        cancelLabel={"отмена"}
        isProcessing={isDestroying}
        onCancel={closeDestroyConfirm}
        onConfirm={confirmDestroyProduct}
      />
    </Layout>
  );
});
